import { Card } from '@monorepo/base/src/components/card/card';
import { observer } from 'mobx-react';
import styles from './bidder-targeting.module.scss';
import { DataAttribute } from '@monorepo/tools/src/lib/models/data-attr.model';
import { Input } from '@monorepo/base/src/components/form/input/input';
import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { IAdminxStore } from '../../../../stores';
import { FormError } from '@monorepo/base/src/components/form/form-error/form-error';
import { ButtonGroup, GButton } from '@monorepo/base/src/components/buttons/group/button-group';
import { bidderCampaignTypeToTitle, CampaignTargetingLinkChoicePolicy } from '../../../../models/bidder-campaign/bidder-campaign.types';
import { Toggle } from '@monorepo/base/src/components/form/toggle/toggle';
import { Statuses } from '../../../../enums/status';
import { constantCase, sentenceCase } from 'change-case';
import { Spacer } from '@monorepo/base/src/components/spacer/spacer';
import { BidderCampaignTargetingModel } from '../../../../models/bidder-campaign/bidder-campaign-targeting.model';
import { BidderCampaignTargetingLinkModel } from '../../../../models/bidder-campaign/bidder-campaign-targeting-link.model';
import { TrafficEntityModel } from '../../../../models/traffic-entity.model';
import { id } from '@monorepo/tools/src/lib/types/primitives';
import { Fragment, useEffect, useState } from 'react';
import { TargetingLinksTable } from './bidder-targeting-links-table';
import { DeviceTypes } from '@monorepo/tools/src/lib/enums/device-types';
import { DropdownNew } from '@monorepo/base/src/components/dropdown-new/dropdown-new';
import { PrimaryText } from '@monorepo/base/src/components/buttons/buttons';
import { Dropdown } from '@monorepo/base/src/components/dropdown/dropdown';

type StatusFilter = 'all' | 'active' | 'paused' | 'cache';
type GroupBy = 'none' | 'publisher' | 'status' | 'cache';

const STATUS_OPTIONS = ['All', 'Active', 'Paused', 'Paused in cache'];
const GROUP_BY_OPTIONS = ['None', 'Publisher', 'Status', 'Paused in cache'];

function statusFilterFromLabel(label: string): StatusFilter {
	if (label === 'Active') {
		return 'active';
	}
	if (label === 'Paused') {
		return 'paused';
	}
	if (label === 'Paused in cache') {
		return 'cache';
	}
	return 'all';
}

const splitCodes = (value: string) =>
	value
		.split(',')
		.map(code => code.trim())
		.filter(Boolean);

function uniqueCodes(values: string[]) {
	const seen = new Set<string>();
	const unique: string[] = [];
	values.forEach(value => {
		splitCodes(value).forEach(code => {
			if (seen.has(code)) {
				return;
			}
			seen.add(code);
			unique.push(code);
		});
	});
	return unique.join(',');
}

function applyTrafficFields(targeting: BidderCampaignTargetingModel, entities: TrafficEntityModel[]) {
	targeting.country_code = uniqueCodes(entities.map(entity => entity.geo));
	targeting.os = uniqueCodes(entities.map(entity => entity.op_system));
	targeting.browser = uniqueCodes(entities.map(entity => entity.browser));
	targeting.device_type = uniqueCodes(entities.map(entity => entity.device_type));
	targeting.excluded_browser = '';
}

function optionsForCodes(codes: string[], labeled: { value: string; label: string }[]) {
	const byValue = new Map(labeled.map(option => [option.value, option]));
	return codes.map(code => byValue.get(code) || { value: code, label: code });
}

function groupByFromLabel(label: string): GroupBy {
	if (label === 'Publisher') {
		return 'publisher';
	}
	if (label === 'Status') {
		return 'status';
	}
	if (label === 'Paused in cache') {
		return 'cache';
	}
	return 'none';
}

function groupByToLabel(groupBy: GroupBy): string {
	if (groupBy === 'publisher') {
		return 'Publisher';
	}
	if (groupBy === 'status') {
		return 'Status';
	}
	if (groupBy === 'cache') {
		return 'Paused in cache';
	}
	return 'None';
}

function matchesLinkFilter(
	link: BidderCampaignTargetingLinkModel,
	search: string,
	statusFilter: StatusFilter,
	publisherNameByPid: Record<string, string>
): boolean {
	const q = search.trim().toLowerCase();
	if (q) {
		const publisherName = link.pid != null && link.pid !== '' ? publisherNameByPid[String(link.pid)] : undefined;
		const haystack = [link.yid, link.dest_link, publisherName, String(link.pid ?? ''), link.rh_pid]
			.filter(Boolean)
			.join(' ')
			.toLowerCase();
		if (!haystack.includes(q)) {
			return false;
		}
	}
	if (statusFilter === 'active') {
		return link.status === Statuses.Active;
	}
	if (statusFilter === 'paused') {
		return link.status === Statuses.Paused;
	}
	if (statusFilter === 'cache') {
		return link.paused_in_cache === 1;
	}
	return true;
}

type LinkGroup = {
	key: string;
	title: string;
	links: BidderCampaignTargetingLinkModel[];
};

function groupLinks(links: BidderCampaignTargetingLinkModel[], groupBy: GroupBy): LinkGroup[] {
	if (groupBy === 'none') {
		return links.length ? [{ key: 'all', title: '', links }] : [];
	}

	const buckets = new Map<string, { title: string; links: BidderCampaignTargetingLinkModel[] }>();

	links.forEach(link => {
		let key: string;
		let title: string;

		if (groupBy === 'publisher') {
			const rhPid = link.rh_pid?.trim() ? link.rh_pid.trim() : '';
			key = rhPid || 'none';
			title = rhPid ? `RH Publisher - (${rhPid})` : 'No RH publisher';
		} else if (groupBy === 'status') {
			const isActive = link.status === Statuses.Active;
			key = isActive ? 'active' : 'paused';
			title = isActive ? 'Active' : 'Paused';
		} else {
			const inCache = link.paused_in_cache === 1;
			key = inCache ? 'cache' : 'not-cache';
			title = inCache ? 'Paused in cache' : 'Not paused in cache';
		}

		const bucket = buckets.get(key);
		if (bucket) {
			bucket.links.push(link);
		} else {
			buckets.set(key, { title, links: [link] });
		}
	});

	return Array.from(buckets.entries()).map(([key, { title, links: groupLinksList }]) => ({
		key,
		title: `${title} · ${groupLinksList.length} link${groupLinksList.length === 1 ? '' : 's'}`,
		links: groupLinksList,
	}));
}

export const BidderTargeting = observer(() => {
	const { bidderStore } = useStores<IAdminxStore>();
	const bidderCrudStore = bidderStore.getCrud();
	const inputOptions = bidderStore.getInputOptions();
	const formStore = bidderCrudStore.getFormStore();
	const campaign = bidderCrudStore.getData().campaign;
	const targetings = campaign.campaign_targeting || [];
	const targetingPolicy = campaign.campaign_targeting_link_choice_policy;

	const [linkSearch, setLinkSearch] = useState('');
	const [linkStatusFilter, setLinkStatusFilter] = useState<StatusFilter>('all');
	const [linkGroupBy, setLinkGroupBy] = useState<GroupBy>(() =>
		campaign.campaign_targeting_link_choice_policy === CampaignTargetingLinkChoicePolicy.PerPublisher ? 'publisher' : 'none'
	);

	useEffect(() => {
		bidderStore.trafficList.fetch();
	}, []);

	const trafficOptions = bidderStore.trafficList.getList().flatMap(entity => {
		if (entity.id === undefined) {
			return [];
		}
		return [
			{
				value: String(entity.id),
				label: `${entity.id} — ${entity.geo} / ${entity.op_system} / ${entity.browser} / ${entity.device_type}`,
			},
		];
	});
	const trafficById = new Map(
		bidderStore.trafficList.getList().flatMap(entity => (entity.id === undefined ? [] : [[String(entity.id), entity] as const]))
	);
	const trafficIdsKey = targetings.map(targeting => targeting.traffic_ids).join('|');

	useEffect(() => {
		if (bidderStore.trafficList.getIsLoading() || trafficById.size === 0) {
			return;
		}
		targetings.forEach(targeting => {
			const ids = splitCodes(targeting.traffic_ids);
			if (!ids.length) {
				applyTrafficFields(targeting, []);
				return;
			}
			const entities = ids.flatMap(trafficId => {
				const entity = trafficById.get(trafficId);
				return entity ? [entity] : [];
			});
			if (!entities.length) {
				return;
			}
			applyTrafficFields(targeting, entities);
		});
	}, [bidderStore.trafficList.getIsLoading(), trafficById.size, trafficIdsKey]);

	const publisherNamesMapById = bidderStore.publishersList.getData()?.publishers.reduce(
		(acc, publisher) => {
			acc[publisher.id] = publisher.name;
			return acc;
		},
		{} as Record<id, string>
	);

	const publisherNameByPid: Record<string, string> = {
		...(publisherNamesMapById ? Object.fromEntries(Object.entries(publisherNamesMapById).map(([k, v]) => [String(k), v])) : {}),
		...(campaign.campaign_publisher_lists?.white_list?.reduce(
			(acc, publisher) => {
				acc[String(publisher.pid)] = publisher.publisher_name;
				return acc;
			},
			{} as Record<string, string>
		) || {}),
	};

	return (
		<div className={styles.targetings}>
			<Card
				className={styles.targetingList}
				title={`Targetings`}
				id={'bidderTargetings'}
				icon={'target-05'}
				debugProps={{ dataAttrs: [new DataAttribute('id', 'bidder_targeting_section')] }}>
				<Spacer />
				<ButtonGroup
					className={styles.performancePixelTypeWrapper}
					title={'Targeting link policy'}
					required
					defaultValue={targetingPolicy}
					error={formStore.getErrors().get('campaign_targeting_link_choice_policy')?.getMsg()}
					onChange={setValues => {
						const nextPolicy = setValues.values().next().value;
						campaign.campaign_targeting_link_choice_policy = nextPolicy as CampaignTargetingLinkChoicePolicy;
						if (nextPolicy === CampaignTargetingLinkChoicePolicy.PerPublisher) {
							setLinkGroupBy('publisher');
						}
						formStore.clearError('campaign_targeting_link_choice_policy');
					}}>
					<GButton
						text={bidderCampaignTypeToTitle.get(CampaignTargetingLinkChoicePolicy.PerPublisher)}
						value={CampaignTargetingLinkChoicePolicy.PerPublisher}
					/>
					<GButton
						text={bidderCampaignTypeToTitle.get(CampaignTargetingLinkChoicePolicy.ByWeight)}
						value={CampaignTargetingLinkChoicePolicy.ByWeight}
					/>
					<GButton
						text={bidderCampaignTypeToTitle.get(CampaignTargetingLinkChoicePolicy.WithConversionLimit)}
						value={CampaignTargetingLinkChoicePolicy.WithConversionLimit}
					/>
				</ButtonGroup>
				{/* <Spacer /> */}

				{targetings.map((targeting, index) => {
					const filteredLinks = (targeting.campaign_targeting_links || []).filter(link =>
						matchesLinkFilter(link, linkSearch, linkStatusFilter, publisherNameByPid)
					);
					const linkGroups = groupLinks(filteredLinks, linkGroupBy);
					const hasVisibleLinks = filteredLinks.length > 0;

					return (
						<Fragment key={index}>
							<Card
								collapsible
								className={styles.targetingItem}
								title={`Targeting - ${targeting.id || 'NEW'}`}
								titleChildren={
									<Toggle
										label={
											targeting.status === Statuses.Active
												? sentenceCase(Statuses.Active)
												: sentenceCase(Statuses.Paused)
										}
										onChange={status => {
											targeting.status = status ? Statuses.Active : Statuses.Paused;
										}}
										defaultValue={targeting.status === Statuses.Active}
										debugProps={{ dataAttrs: [new DataAttribute('id', 'campaign_status')] }}
									/>
								}
								defaultCollapsed={targetings.length > 1 && !!targeting.id}>
								<div className={styles.inputsWrapper}>
									<div className={styles.inputWrapper}>
										<Input
											type={'number'}
											className={`${styles.inputWithHint}`}
											value={targeting.estimated_rate ? targeting.estimated_rate?.toString() : '0'}
											placeholder={'Estimated Rate'}
											onValue={value => {
												targeting.estimated_rate = parseFloat(value);
												formStore.clearError('estimated_rate');
											}}
											debugProps={{ dataAttrs: [new DataAttribute('id', 'estimated_rate')] }}
										/>
										<FormError
											className={styles.fromError}
											msg={formStore.getErrors().get('estimatedRate')?.getMsg()}
										/>
									</div>
									<div className={styles.inputWrapper}>
										<Input
											type={'number'}
											className={`${styles.inputWithHint}`}
											value={targeting.q_estimated_rate ? targeting.q_estimated_rate?.toString() : '0'}
											placeholder={'Q Estimated Rate'}
											onValue={value => {
												targeting.q_estimated_rate = parseFloat(value);
												formStore.clearError('q_estimated_rate');
											}}
											debugProps={{ dataAttrs: [new DataAttribute('id', 'q_estimated_rate')] }}
										/>
										<FormError
											className={styles.fromError}
											msg={formStore.getErrors().get('qEstimatedRate')?.getMsg()}
										/>
									</div>
								</div>

								<DropdownNew
									classNames={{
										dropdownWrapper: `${styles.dropdown} ${styles.w100}`,
										listItem: styles.listItem,
										dropdownInput: `${styles.dropdownInput} ${styles.w100}`,
										autocompleteInputWrapper: styles.w100,
									}}
									required
									multi
									autocomplete
									autocompletePlaceholder="Search traffic"
									label="Traffic IDs"
									defaultValues={splitCodes(targeting.traffic_ids)}
									options={trafficOptions}
									onSelect={trafficIds => {
										targeting.traffic_ids = trafficIds.filter(Boolean).join(',');
										formStore.clearError('traffic_ids');
									}}
									error={formStore.getErrors().get('traffic_ids')?.getMsg()}
									debugProps={{ dataAttrs: [new DataAttribute('id', 'traffic_ids')] }}
								/>

								<ButtonGroup multi required title="Device Type" defaultValues={new Set(splitCodes(targeting.device_type))}>
									<GButton
										disabled
										icon={'phone-android'}
										text={DeviceTypes.Mobile}
										value={constantCase(DeviceTypes.Mobile)}
										debugProps={{ dataAttrs: [new DataAttribute('id', 'device_types_mobile')] }}
									/>
									<GButton
										disabled
										icon={'tablet-02'}
										text={DeviceTypes.Tablet}
										value={constantCase(DeviceTypes.Tablet)}
										debugProps={{ dataAttrs: [new DataAttribute('id', 'device_types_tablet')] }}
									/>
									<GButton
										disabled
										icon={'monitor-01'}
										text={DeviceTypes.Computer}
										value={constantCase(DeviceTypes.Computer)}
										debugProps={{ dataAttrs: [new DataAttribute('id', 'device_types_computer')] }}
									/>
								</ButtonGroup>

								<DropdownNew
									classNames={{
										dropdownWrapper: `${styles.dropdown} ${styles.w100}`,
										listItem: styles.listItem,
										dropdownInput: `${styles.dropdownInput} ${styles.w100}`,
										autocompleteInputWrapper: styles.w100,
									}}
									disabled
									multi
									label="OS"
									defaultValues={splitCodes(targeting.os)}
									options={optionsForCodes(
										splitCodes(targeting.os),
										inputOptions
											.getOperatingSystems()
											.filter(i => i.to_target)
											.map(i => ({
												value: i.code,
												label: i.name,
											}))
									)}
									debugProps={{ dataAttrs: [new DataAttribute('id', 'os')] }}
								/>

								<DropdownNew
									classNames={{
										dropdownWrapper: `${styles.dropdown} ${styles.w100}`,
										listItem: styles.listItem,
										dropdownInput: `${styles.dropdownInput} ${styles.w100}`,
										autocompleteInputWrapper: styles.w100,
									}}
									disabled
									multi
									label="Browsers"
									defaultValues={splitCodes(targeting.browser)}
									options={optionsForCodes(
										splitCodes(targeting.browser),
										inputOptions
											.getBrowsers()
											.filter(i => i.to_target)
											.map(i => ({
												value: i.code,
												label: i.name,
											}))
									)}
									debugProps={{ dataAttrs: [new DataAttribute('id', 'browser')] }}
								/>

								<DropdownNew
									classNames={{
										dropdownWrapper: `${styles.dropdown} ${styles.w100}`,
										listItem: styles.listItem,
										dropdownInput: `${styles.dropdownInput} ${styles.w100}`,
										autocompleteInputWrapper: styles.w100,
									}}
									disabled
									multi
									defaultValues={splitCodes(targeting.country_code)}
									label="Geo"
									options={optionsForCodes(
										splitCodes(targeting.country_code),
										inputOptions.getGeos().map(i => ({
											label: `${i.name} - (${i.code})`,
											value: i.code,
										}))
									)}
									debugProps={{ dataAttrs: [new DataAttribute('id', 'country_code')] }}
									isLabelOnBorder
								/>

								<Spacer />
								<div className={styles.linksSectionTitle}>Targeting Links</div>

								<div className={styles.linksToolbar}>
									<Dropdown
										className={styles.statusDropdown}
										label="Status"
										options={STATUS_OPTIONS}
										defaultOption="All"
										onSelect={val => {
											const label = val ? [...val][0] : 'All';
											setLinkStatusFilter(statusFilterFromLabel(label || 'All'));
										}}
										isCloseIcon={false}
									/>
									<Dropdown
										key={linkGroupBy}
										label="Group by"
										options={GROUP_BY_OPTIONS}
										defaultOption={groupByToLabel(linkGroupBy)}
										onSelect={val => {
											const label = val ? [...val][0] : 'None';
											setLinkGroupBy(groupByFromLabel(label || 'None'));
										}}
										isCloseIcon={false}
									/>
									<div>
										<Input
											placeholder="Search links"
											value={linkSearch}
											onValue={value => {
												setLinkSearch(value);
											}}
											className={styles.linksToolbarSearch}
										/>
									</div>
								</div>

								{!hasVisibleLinks ? <div className={styles.linksEmptyState}>No links match filters</div> : null}

								{linkGroupBy === 'none' ? (
									<TargetingLinksTable
										campaignId={campaign.campaign_id}
										targeting={targeting}
										links={filteredLinks}
										policy={targetingPolicy}
									/>
								) : (
									linkGroups.map(group => (
										<Card className={styles.targetingLinkWrapper} collapsible title={group.title} key={group.key}>
											<Spacer />
											<TargetingLinksTable
												campaignId={campaign.campaign_id}
												targeting={targeting}
												links={group.links}
												policy={targetingPolicy}
											/>
										</Card>
									))
								)}

								<PrimaryText icon="plus" onClick={() => targeting.addNewLink()}>
									Add Targeting Link
								</PrimaryText>
							</Card>

							{targetings.length > index + 1 && <Spacer />}
						</Fragment>
					);
				})}

				<Spacer />

				<PrimaryText icon="plus" onClick={() => campaign.addTargeting()}>
					Add Targeting
				</PrimaryText>
			</Card>
		</div>
	);
});
